import { useEffect, useMemo } from 'react';
import { useDeviceStore } from '../store/device';
import { useSyncedStore } from '../store/synced';
import { applyTheme } from './applyTheme';
import { buildCustomTheme } from './customTheme';
import { CUSTOM_THEME_KEY, DEFAULT_THEME, getTheme } from './registry';
import { resolveThemeKey } from './resolveThemeKey';
import type { Theme } from './types';

// Resolves the currently-active profile's theme, applies it to <html>,
// and returns the Theme so callers can render its decoration.
//
// "Active profile" is the device-local activeProfileId set by useActiveProfile
// on each kid-page mount. Falls back to the default theme when no profile
// is active (home screen, settings).
//
// v2 §D4: routes through `resolveThemeKey(profile)`. With no routineId
// passed, this is `profile.theme.default` today; step 4 will wire the
// active routine so per-routine overrides take effect without changes
// to consumers.
export function useActiveTheme(): Theme {
  const activeProfileId = useDeviceStore((s) => s.activeProfileId);
  const themeKey = useSyncedStore((s) => {
    if (!activeProfileId) return null;
    const profile = s.tree?.profiles.find((p) => p.id === activeProfileId);
    return profile ? resolveThemeKey(profile) : null;
  });
  const customRecipeJson = useSyncedStore((s) => {
    if (!activeProfileId) return null;
    const profile = s.tree?.profiles.find((p) => p.id === activeProfileId);
    if (!profile?.theme.custom) return null;
    return JSON.stringify(profile.theme.custom);
  });

  const theme = useMemo<Theme>(() => {
    if (themeKey !== CUSTOM_THEME_KEY) return getTheme(themeKey);
    if (!customRecipeJson) return DEFAULT_THEME;
    try {
      return buildCustomTheme(JSON.parse(customRecipeJson));
    } catch {
      return DEFAULT_THEME;
    }
  }, [themeKey, customRecipeJson]);

  useEffect(() => applyTheme(theme), [theme]);

  return theme;
}
